import type { AnyFieldMeta } from "@tanstack/react-form";

type FieldErrorsProps = {
	meta: AnyFieldMeta;
};

function normalizeFieldErrorMessage(error: unknown): string[] {
	if (typeof error === "string") {
		const trimmed = error.trim();
		return trimmed.length > 0 ? [trimmed] : [];
	}

	if (Array.isArray(error)) {
		return error.flatMap(normalizeFieldErrorMessage);
	}

	if (typeof error === "object" && error !== null) {
		const message = (error as { message?: unknown }).message;
		if (typeof message === "string") {
			const trimmed = message.trim();
			return trimmed.length > 0 ? [trimmed] : [];
		}

		const issues = (error as { issues?: unknown[] }).issues;
		if (Array.isArray(issues)) {
			return issues.flatMap(normalizeFieldErrorMessage);
		}

		return [JSON.stringify(error)];
	}

	const trimmed = String(error).trim();
	return trimmed.length > 0 ? [trimmed] : [];
}

/** Normalized user-visible messages (whitespace-only entries removed). */
export function getVisibleFieldErrorMessages(meta: AnyFieldMeta): string[] {
	if (!meta.errors?.length) return [];
	return meta.errors
		.flatMap(normalizeFieldErrorMessage)
		.filter((message) => message.length > 0);
}

export const FieldErrors = ({ meta }: FieldErrorsProps) => {
	// Don't show errors until the field has been interacted with (blur/change).
	// Submit paths mark fields touched via validateAllFields, so submit errors still appear.
	if (!meta.isTouched && !meta.isDirty) return null;
	if (meta.isValid || !meta.errors || meta.errors.length === 0) return null;

	const errorMessages = getVisibleFieldErrorMessages(meta);
	if (errorMessages.length === 0) return null;

	return (
		<ul
			className="mt-1.5 text-sm font-medium text-error-500"
			aria-label="Field errors"
		>
			{errorMessages.map((errorMessage) => (
				<li key={errorMessage} aria-label={`Field error: ${errorMessage}`}>
					{errorMessage}
				</li>
			))}
		</ul>
	);
};
